import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

/**
 * Programs landing.
 *
 * Desktop (>=768px): background image already contains the title/subtitle baked
 *   into the wall. The 4 program cards float over it, styled dark-on-warm.
 *
 * Mobile (<768px, Sprint 48): dedicated warm-room background. Title + subtitle
 *   are HTML overlays in ivory. 4 cards are chocolate-brown (#4A3428) in a 2×2
 *   grid with ivory typography. Non-breaking spaces are used to prevent orphan
 *   words like "Я" from wrapping alone onto a last line.
 *
 * Desktop styling is intentionally unchanged.
 */
const BACKGROUND =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_4fdfd6f9-6803-4276-8258-f36ae827b1f3/artifacts/szj8ewyu_ChatGPT%20Image%2015%20%D0%B0%D0%B2%D0%B3.%202026%20%D0%B3.%2C%2002_57_16.png";

// Sprint 48: approved 9:16 warm-room mobile background (arched window, cabinet).
const MOBILE_BACKGROUND =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_4fdfd6f9-6803-4276-8258-f36ae827b1f3/artifacts/ui0ph0k6_89BE1895-5E7F-4209-AD13-F900A6737EAB.png";

// Titles use \u00A0 (non-breaking spaces) to keep visually-linked tokens together
// on mobile and prevent single-letter/short-word orphans on the last line.
const PROGRAMS = [
  {
    slug: "almond",
    title: "Том\u00A0I «Миндаль»",
    subtitle: "Логика вкуса, понимание сочетаний и\u00A0текстур",
  },
  {
    slug: "medovik",
    title: "Медовик как\u00A0бизнес",
    subtitle: "Авторская линейка современных медовиков",
  },
  {
    slug: "bento",
    title: "Бенто-торты от\u00A0А\u00A0до\u00A0Я",
    subtitle: "Объёмные рисунки и\u00A0фигурки из\u00A0крема",
  },
  {
    slug: "lineart",
    title: "Контурные рисунки на\u00A0бенто",
    subtitle: "Стиль line-art, лаконичность и\u00A0современность",
  },
];

export default function ProgramsLanding() {
  const nav = useNavigate();
  return (
    <div className="programs-scene relative min-h-screen w-full overflow-hidden" data-testid="programs-landing-page">
      {/* Desktop background (baked-in title) — hidden on mobile */}
      <img
        src={BACKGROUND}
        alt=""
        aria-hidden
        draggable={false}
        className="hidden md:block absolute inset-0 w-full h-full pointer-events-none select-none"
        style={{ objectFit: "cover", objectPosition: "center center" }}
      />

      {/* Mobile background (Sprint 48) — warm empty room with arched window */}
      <img
        src={MOBILE_BACKGROUND}
        alt=""
        aria-hidden
        draggable={false}
        className="md:hidden absolute inset-0 w-full h-full pointer-events-none select-none"
        style={{ objectFit: "cover", objectPosition: "50% 30%" }}
        data-testid="programs-mobile-bg"
      />
      {/* Warm veil for card contrast on the mobile photo */}
      <div
        className="md:hidden absolute inset-0 pointer-events-none"
        aria-hidden
        style={{
          background:
            "linear-gradient(180deg, rgba(58,40,30,0.22) 0%, rgba(58,40,30,0.05) 26%, rgba(58,40,30,0.10) 62%, rgba(58,40,30,0.38) 100%)",
        }}
      />

      {/* Back to hall */}
      <button
        onClick={() => nav("/hall")}
        data-testid="back-to-hall-button"
        aria-label="В главный зал"
        className="absolute top-5 left-5 md:top-6 md:left-2 lg:left-3 z-20 w-10 h-10 md:w-10 md:h-10 rounded-full bg-white/70 backdrop-blur border border-[#C9B79A]/50 flex items-center justify-center hover:bg-white transition-colors shadow-[0_2px_10px_rgba(74,59,50,0.10)]"
      >
        <ChevronLeft className="w-5 h-5 text-[#4A3B32]" />
      </button>

      {/* Mobile heading — desktop keeps baked-in title on background */}
      <div className="md:hidden relative z-10 pt-[68px] pb-3 px-5 text-center">
        <h1
          className="font-serif leading-[1.1] text-[26px] text-[#FFF8EE] drop-shadow-[0_2px_6px_rgba(58,40,30,0.45)]"
          data-testid="programs-mobile-title"
        >
          Программы<br />Anokina&nbsp;Studio
        </h1>
        <p
          className="mt-1.5 text-[12px] tracking-[0.14em] uppercase text-[#F0E4D4] drop-shadow-[0_1px_3px_rgba(58,40,30,0.4)]"
          data-testid="programs-mobile-subtitle"
        >
          Обучение и&nbsp;практика
        </p>
      </div>

      {/*
        Cards grid.
        Mobile: 2×2, chocolate cards on warm-room photo, ~150–160px tall,
                outer padding 20px, gap 12px.
        Desktop: unchanged — dark cards shifted right of the arched window.
      */}
      <div className="relative z-10 mx-auto w-full max-w-[1240px] px-5 md:px-12 lg:px-16 pt-3 md:pt-[36vh] lg:pt-[38vh] pb-8 md:pb-14">
        <div
          className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-x-7 md:gap-y-7 lg:gap-x-8 lg:gap-y-8 md:max-w-[600px] lg:max-w-[660px] md:ml-[22%] lg:ml-[20%]"
          data-testid="programs-grid"
        >
          {PROGRAMS.map((p) => (
            <ProgramCard key={p.slug} program={p} onOpen={() => nav(`/programs/${p.slug}`)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ProgramCard({ program, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`program-card-${program.slug}`}
      className="program-card group relative text-left rounded-[14px] px-4 py-4 md:px-6 md:py-6 h-[156px] md:h-auto md:min-h-[164px] transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EADFC7]/50 bg-[#4A3428] md:bg-transparent border border-[#3A281E]/70 md:border-0 shadow-[0_6px_18px_rgba(58,40,30,0.28)] md:shadow-none flex md:block flex-col justify-start"
    >
      <h3
        className="font-serif leading-[1.15] text-[16px] md:text-[22px] text-[#FFF8EE] md:text-[#F5EAD5] break-words hyphens-none"
      >
        {program.title}
      </h3>
      <p className="mt-1.5 md:mt-2.5 leading-snug text-[11.5px] md:text-[13.5px] text-[#E8DCCB] md:text-[#D8C7A8]/90 break-words hyphens-none">
        {program.subtitle}
      </p>
    </button>
  );
}
